import { router, usePage } from '@inertiajs/vue3';
import { useThrottleFn } from '@vueuse/core';
import type { Agency, ChatConversation, ChatMessage, ChatMessagePart, ChatToolInvocation, StreamEvent, SubscriberList } from 'types';
import { type Ref, computed, nextTick, onMounted, onUnmounted, ref } from 'vue';
import axios from '../axios';
import { useBeeMcpSession } from './useBeeMcpSession';

function appendText(parts: ChatMessagePart[], delta: string): void {
    const last = parts.at(-1);

    if(last?.kind === 'text') {
        last.text += delta;
        return;
    }

    parts.push({ kind: 'text', text: delta });
}

function appendToolInvocation(parts: ChatMessagePart[], name: string, invocation: ChatToolInvocation): void {
    const last = parts.at(-1);

    // Only collapse consecutive calls to the same tool into one pill (×N).
    // A text part between two calls ends the pill — that's how we get the
    // "text → tool → text → tool" flow instead of "all text then all tools".
    if(last?.kind === 'tool' && last.name === name) {
        last.invocations.push(invocation);
        return;
    }

    parts.push({ kind: 'tool', name, invocations: [invocation] });
}

function updateToolOutput(parts: ChatMessagePart[], toolCallId: string, output: string): void {
    for(const part of parts) {
        if(part.kind !== 'tool') {
            continue;
        }

        const inv = part.invocations.find(i => i.id === toolCallId);

        if(inv) {
            inv.output = output;
            inv.status = 'complete';
            return;
        }
    }
}

const STORAGE_KEY = 'alchemy_chat_conversation_id';

function getCsrfToken(): string {
    const match = document.cookie.match(/XSRF-TOKEN=([^;]+)/);

    return match ? decodeURIComponent(match[1]) : '';
}

function generateId(): string {
    return crypto.randomUUID();
}

type PageContext = {
    page: string;
    url: string;
    agency?: string;
    agency_id?: number;
    agency_slug?: string;
    list?: string;
    list_id?: number;
    list_slug?: string;
    bee_template_id?: string;
    bee_template_snapshot?: string;
};

const suggestionMap: Record<string, string[]> = {
    Home: ['List my agencies', 'Show recent sends'],
    Dashboard: ['Show sends for this list', 'How many subscribers are on this list?'],
    SendIndex: ['Show draft sends', 'How many sends are scheduled?'],
    SendShow: ['Show details for this send', 'What is the send status?'],
    SendCreate: ['List available templates', 'Show template details'],
    SendEdit: ['List available templates', 'Show template details'],
    SubscriberIndex: ['Search for a subscriber', 'How many subscribers total?'],
    SubscriberShow: ['Search for a subscriber by email', 'Show subscriber details'],
    TemplateIndex: ['List templates', 'Create a new template'],
    TemplateShow: ['Show template details', 'List sends using this template'],
    AgencyIndex: ['List subscriber lists', 'Show agency details'],
    AgencyShow: ['List subscriber lists for this agency', 'Show agency users'],
};

const defaultSuggestions = ['List my agencies', 'Show my subscriber lists', 'Help me get started'];

type StreamEventOf<T extends StreamEvent['type']> = Extract<StreamEvent, { type: T }>;

type StreamEventContext<T extends StreamEvent['type']> = {
    event: StreamEventOf<T>;
    assistant: ChatMessage | undefined;
    conversationId: Ref<string | null>;
    error: Ref<string | null>;
};

type StreamEventHandler<T extends StreamEvent['type']> = (ctx: StreamEventContext<T>) => void;

function onConversationId({ event, conversationId }: StreamEventContext<'conversation_id'>) {
    conversationId.value = event.id;
    localStorage.setItem(STORAGE_KEY, event.id);
}

function onTextDelta({ event, assistant }: StreamEventContext<'text-delta'>) {
    if(assistant) {
        appendText(assistant.parts, event.delta);
    }
}

function onToolInputAvailable({ event, assistant }: StreamEventContext<'tool-input-available'>) {
    if(!assistant) {
        return;
    }

    appendToolInvocation(assistant.parts, event.toolName.replace(/^relay__\w+__/, ''), {
        id: event.toolCallId,
        input: event.input,
        status: 'running',
    });
}

function onToolOutputAvailable({ event, assistant }: StreamEventContext<'tool-output-available'>) {
    if(assistant) {
        updateToolOutput(assistant.parts, event.toolCallId, event.output);
    }
}

function onNavigate({ event }: StreamEventContext<'navigate'>) {
    router.visit(event.url);
}

function onError({ event, error }: StreamEventContext<'error'>) {
    error.value = event.errorText;
}

const streamEventHandlers: { [K in StreamEvent['type']]?: StreamEventHandler<K> } = {
    'conversation_id': onConversationId,
    'text-delta': onTextDelta,
    'tool-input-available': onToolInputAvailable,
    'tool-output-available': onToolOutputAvailable,
    'navigate': onNavigate,
    'error': onError,
};

export function useChatBot() {
    const page = usePage<{ agency?: Agency; list?: SubscriberList }>();
    const beeMcpSession = useBeeMcpSession();

    const pageContext = computed<PageContext>(() => ({
        page: page.component,
        url: page.url,
        agency: page.props.agency?.name,
        agency_id: page.props.agency?.id,
        agency_slug: page.props.agency?.slug,
        list: page.props.list?.name,
        list_id: page.props.list?.id,
        list_slug: page.props.list?.slug,
        bee_template_id: beeMcpSession.templateId.value ?? undefined,
        bee_template_snapshot: beeMcpSession.templateSnapshot.value ?? undefined,
    }));

    const suggestions = computed(() => {
        const component = page.component.replace(/^.*\//, '');

        return suggestionMap[component] ?? defaultSuggestions;
    });

    const messages = ref<ChatMessage[]>([]);
    const conversationId = ref<string | null>(localStorage.getItem(STORAGE_KEY));
    const conversations = ref<ChatConversation[]>([]);
    const showHistory = ref(false);
    const isLoadingHistory = ref(false);
    const isOpen = ref(false);
    const isStreaming = ref(false);
    const isFetching = ref(false);
    const error = ref<string | null>(null);
    const messagesContainer = ref<HTMLElement | null>(null);

    let abortController: AbortController | null = null;

    function scrollToBottom() {
        nextTick(() => {
            if(messagesContainer.value) {
                messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
            }
        });
    }

    const throttledScroll = useThrottleFn(scrollToBottom, 100);

    onUnmounted(() => {
        abortController?.abort();
    });

    function handleStreamEvent(event: StreamEvent) {
        const handler = streamEventHandlers[event.type];
        const assistant = messages.value.at(-1);

        handler?.({
            event: event as never,
            assistant: assistant?.role === 'assistant' ? assistant : undefined,
            conversationId,
            error,
        });

        throttledScroll();
    }

    async function streamResponse(message: string) {
        abortController = new AbortController();
        isFetching.value = true;
        isStreaming.value = true;

        try {
            await beeMcpSession.start();

            const response = await fetch('/chat', {
                method: 'POST',
                credentials: 'same-origin',
                signal: abortController.signal,
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'text/plain',
                    'X-XSRF-TOKEN': getCsrfToken(),
                },
                body: JSON.stringify({
                    message,
                    conversation_id: conversationId.value,
                    page_context: pageContext.value,
                }),
            });

            if(!response.ok) {
                const text = await response.text();

                if(response.status === 422) {
                    try {
                        const body = JSON.parse(text) as { errors?: Record<string, string[]> };
                        const firstError = Object.values(body.errors ?? {})[0]?.[0];
                        error.value = firstError ?? 'Your message was rejected by the server.';
                    } catch {
                        error.value = 'Your message was rejected by the server.';
                    }

                    return;
                }

                error.value = text.includes('Page Expired') ? 'Session expired. Please refresh.' : `Error: ${response.status}`;

                return;
            }

            const id = response.headers.get('X-Conversation-Id');
            if(id) {
                conversationId.value = id;
                localStorage.setItem(STORAGE_KEY, id);
            }

            isFetching.value = false;

            const reader = response.body?.getReader();

            if(!reader) {
                return;
            }

            const decoder = new TextDecoder();
            let buffer = '';
            let sawDone = false;

            function consume(line: string) {
                const trimmed = line.trim();

                if(!trimmed || !trimmed.startsWith('data: ')) {
                    return;
                }

                const payload = trimmed.slice(6);

                if(!payload) {
                    return;
                }

                if(payload === '[DONE]') {
                    sawDone = true;

                    return;
                }

                try {
                    const event: StreamEvent = JSON.parse(payload);
                    handleStreamEvent(event);
                } catch {
                    // partial JSON, skip
                }
            }

            while(true) {
                const { done, value } = await reader.read();

                if(done) {
                    break;
                }

                buffer += decoder.decode(value, { stream: true });

                const lines = buffer.split('\n');
                buffer = lines.pop() ?? '';

                for(const line of lines) {
                    consume(line);
                }
            }

            // The trailing chunk keeps whatever followed the last newline. The
            // server terminates every frame with a blank line, so this is
            // normally empty — but if that ever changes, the sentinel would sit
            // here unread and a complete response would be reported truncated.
            buffer += decoder.decode();
            consume(buffer);

            // The server writes `data: [DONE]` after the generator finishes.
            // A missing sentinel means the request was killed mid-stream
            // (most commonly PHP's max_execution_time) — warn the user so a
            // truncated response isn't mistaken for a complete one.
            if(!sawDone && !error.value) {
                error.value = 'Response was cut off before finishing. Try a shorter request or split it into steps.';
            }
        } catch (err) {
            if(err instanceof DOMException && err.name === 'AbortError') {
                return;
            }

            error.value = err instanceof Error ? err.message : 'Stream failed';
        } finally {
            isStreaming.value = false;
            isFetching.value = false;
            abortController = null;
        }
    }

    function send(message: string) {
        // Guard against re-entrant sends — the UI already disables the button
        // while streaming, but a second call here would create an orphaned
        // request that ends up persisted against a stale conversation id.
        if(isStreaming.value) {
            return;
        }

        const trimmed = message.trim();
        if(!trimmed) {
            return;
        }

        error.value = null;
        showHistory.value = false;

        messages.value.push({
            id: generateId(),
            role: 'user',
            parts: [{ kind: 'text', text: trimmed }],
            createdAt: new Date().toISOString(),
        });

        messages.value.push({
            id: generateId(),
            role: 'assistant',
            parts: [],
            createdAt: new Date().toISOString(),
        });

        scrollToBottom();
        streamResponse(trimmed);
    }

    function cancel() {
        abortController?.abort();
    }

    function toggle() {
        isOpen.value = !isOpen.value;
    }

    function newConversation() {
        abortController?.abort();
        messages.value = [];
        conversationId.value = null;
        showHistory.value = false;
        localStorage.removeItem(STORAGE_KEY);
    }

    async function loadConversations() {
        isLoadingHistory.value = true;

        try {
            const { data } = await axios.get<ChatConversation[]>('/chat/conversations', {
                baseURL: '/',
            });
            conversations.value = data;
        } finally {
            isLoadingHistory.value = false;
        }
    }

    async function switchConversation(id: string) {
        // Abort any in-flight stream so its persistMessages doesn't land on
        // the wrong thread and then reappear next time the user opens it.
        abortController?.abort();
        isStreaming.value = false;
        isFetching.value = false;

        conversationId.value = id;
        localStorage.setItem(STORAGE_KEY, id);
        showHistory.value = false;

        try {
            // The backend persists a chronologically-ordered `parts` array
            // alongside the flat content + tool_calls (kept for Prism). Use
            // `parts` when present; fall back to rebuilding from flat data for
            // rows written before the migration (or when parts is missing).
            type StoredMessage = {
                id: string;
                role: ChatMessage['role'];
                content: string;
                toolCalls?: { id: string; name: string; arguments: Record<string, unknown> }[];
                parts?: ChatMessagePart[] | null;
                createdAt: string;
            };

            const { data } = await axios.get<StoredMessage[]>(`/chat/conversations/${id}`, {
                baseURL: '/',
            });

            messages.value = data.map((msg): ChatMessage => {
                if(msg.parts && msg.parts.length > 0) {
                    return {
                        id: msg.id,
                        role: msg.role,
                        parts: msg.parts,
                        createdAt: msg.createdAt,
                    };
                }

                const parts: ChatMessagePart[] = [];

                if(msg.content) {
                    parts.push({ kind: 'text', text: msg.content });
                }

                for(const tc of msg.toolCalls ?? []) {
                    appendToolInvocation(parts, tc.name, {
                        id: tc.id,
                        input: tc.arguments,
                        status: 'complete',
                    });
                }

                return {
                    id: msg.id,
                    role: msg.role,
                    parts,
                    createdAt: msg.createdAt,
                };
            });

            scrollToBottom();
        } catch {
            // Treat a failed load as a stale pointer — drop the id so the
            // next send() starts a brand new conversation instead of
            // silently appending to the lost one.
            conversationId.value = null;
            localStorage.removeItem(STORAGE_KEY);
            messages.value = [];
            error.value = 'Failed to load conversation';
        }
    }

    onMounted(() => {
        // conversationId is restored from localStorage so a reload can resume
        // where the user left off — but messages starts empty, so without
        // hydration the chat UI looks like a fresh thread while the backend
        // still treats it as a continuation. Pull the messages to match.
        if(conversationId.value) {
            switchConversation(conversationId.value);
        }
    });

    async function deleteConversation(id: string) {
        try {
            await axios.delete(`/chat/conversations/${id}`, {
                baseURL: '/',
            });

            conversations.value = conversations.value.filter(c => c.id !== id);

            if(conversationId.value === id) {
                newConversation();
            }
        } catch {
            error.value = 'Failed to delete conversation';
        }
    }

    function toggleHistory() {
        showHistory.value = !showHistory.value;

        if(showHistory.value) {
            loadConversations();
        }
    }

    return {
        messages,
        conversationId,
        conversations,
        suggestions,
        showHistory,
        isLoadingHistory,
        isOpen,
        isStreaming,
        isFetching,
        error,
        messagesContainer,
        send,
        cancel,
        toggle,
        newConversation,
        toggleHistory,
        switchConversation,
        deleteConversation,
    };
}
